import { deriveRestApiTriggerRoutesFromFlowSpec } from './deriveRestApiTriggerRoutesFromFlowSpec';
import { pruneRestApiTriggerRoutes } from './endpointRoutesUtils';

import type { ApiRoutes } from '~/api/pipelineV2';
import {
  collectConnectedApiMethodIdsFromFlowSpec,
  flowSpecHasApiRouter
} from '~/scenes/Build/scenes/FlowBuilder/PipelineDesign/helper/collectConnectedApiMethodIds';

export const API_FIRST_NOT_FOUND_TRACK_PREFIX = 'api-first:not-found:' as const;

export function flowSpecHasApiFirstNotFoundTrack(flowSpec: unknown): boolean {
  if (!flowSpec || typeof flowSpec !== 'object') {
    return false;
  }
  return Object.keys(flowSpec as Record<string, unknown>).some(key =>
    key.startsWith(API_FIRST_NOT_FOUND_TRACK_PREFIX)
  );
}

export function flowSpecHasAnyApiFirstTrack(flowSpec: unknown): boolean {
  if (!flowSpec || typeof flowSpec !== 'object') {
    return false;
  }
  return Object.keys(flowSpec as Record<string, unknown>).some(key =>
    key.startsWith('api-first:')
  );
}

export function hasRestApiTriggerRoutes(
  routes: ApiRoutes[] | undefined
): boolean {
  return Array.isArray(routes) && routes.length > 0;
}

function isBlankTriggerSpecRouteValue(value: unknown): boolean {
  return typeof value === 'string' && value.trim() === '';
}

export function triggerSpecLooksLikeApiFirstDraft(
  triggerSpec: PipelineEntityLike['triggerSpec'] | undefined
): boolean {
  if (!triggerSpec || triggerSpec.type !== 'rest' || !triggerSpec.advanced) {
    return false;
  }

  const { uris, methods } = triggerSpec;
  if (
    !Array.isArray(uris) ||
    uris.length === 0 ||
    !uris.every(isBlankTriggerSpecRouteValue)
  ) {
    return false;
  }

  if (!Array.isArray(methods) || methods.length === 0) {
    return true;
  }

  return methods.every(isBlankTriggerSpecRouteValue);
}

function pipelineHasApiFirstMarkers(pipeline: PipelineEntityLike): boolean {
  return (
    hasRestApiTriggerRoutes(pipeline.restApiTriggerRoutes) ||
    flowSpecHasAnyApiFirstTrack(pipeline.flowSpec)
  );
}

export type PipelineEntityLike = {
  _id?: string;
  id?: string;
  projectId?: string;
  name?: string;
  description?: string;
  /** List/search field from backend (`searchPipelines.content`). */
  hasApiTrigger?: boolean;
  /** Detail/save field; kept for pipelineV2 and legacy payloads. */
  isUsingApiTrigger?: boolean;
  apiExposed?: boolean;
  /** Present when mapped from full pipeline fetch; used to hydrate routes if `restApiTriggerRoutes` is empty. */
  flowSpec?: unknown;
  triggerSpec?: {
    name?: string;
    type?: string;
    apiExposed?: boolean;
    external?: boolean;
    internal?: boolean;
    advanced?: boolean;
    uris?: string[];
    methods?: string[];
    /** JSON object string of header name → value */
    headers?: string;
    /** JSON object string of CORS header name → value */
    corsHeaders?: string;
    keyAuth?: boolean;
    jwt?: boolean;
    jwsAlgorithm?: string;
    externalToken?: Record<string, unknown>;
    externalJWT?: boolean;
    externalJwt?: boolean;
    basicAuth?: boolean;
    timeout?: number;
    requestSizeLimit?: number;
    addCors?: boolean;
    mtls?: boolean;
    allowRedelivery?: boolean;
    enableRateLimit?: boolean;
    /** Rate limit granularity (Digibee API trigger; canvas parity). */
    limitBy?: string;
    aggregateBy?: string;
    /** Legacy connector / GraphQL duplicate keys (canvas); may diverge from limitBy / aggregateBy. */
    rateLimitBy?: string;
    rateLimitAggregateBy?: string;
    /** Rate limit tiers — same as canvas FantaForm “Options”; see Digibee API Trigger docs. */
    options?: Array<{
      intervalValue?: number;
      interval?: string;
      limit?: number;
    }>;
  };
  restApiTriggerRoutes?: ApiRoutes[];
  /** When loading from API, OpenAPI text lives under `trigger` */
  stepsDocumentations?: { trigger?: string } | Record<string, unknown>;
};

export function getPipelineEntityId(
  entity: PipelineEntityLike | null
): string | undefined {
  if (!entity) {
    return undefined;
  }
  if (entity.id) {
    return entity.id;
  }
  return Reflect.get(entity, '_id') as string | undefined;
}

export function mapPipelineRecordToEntityLike(
  pipeline: Record<string, unknown>
): PipelineEntityLike {
  const triggerSpec = pipeline.triggerSpec as
    | Record<string, unknown>
    | undefined;
  const apiExposed =
    typeof triggerSpec?.apiExposed === 'boolean'
      ? triggerSpec.apiExposed
      : undefined;
  const rawSteps = pipeline.stepsDocumentations;
  const stepsDocumentations =
    rawSteps && typeof rawSteps === 'object'
      ? (rawSteps as PipelineEntityLike['stepsDocumentations'])
      : undefined;

  return {
    id: pipeline.id as string | undefined,
    // eslint-disable-next-line no-underscore-dangle -- API field name
    _id: Reflect.get(pipeline, '_id') as string | undefined,
    name: pipeline.name as string | undefined,
    description: pipeline.description as string | undefined,
    projectId: pipeline.projectId as string | undefined,
    hasApiTrigger: pipeline.hasApiTrigger as boolean | undefined,
    isUsingApiTrigger: pipeline.isUsingApiTrigger as boolean | undefined,
    apiExposed,
    triggerSpec: triggerSpec as PipelineEntityLike['triggerSpec'],
    restApiTriggerRoutes: pipeline.restApiTriggerRoutes as
      | ApiRoutes[]
      | undefined,
    flowSpec: pipeline.flowSpec,
    stepsDocumentations
  };
}

export function preparePipelineForApiTriggerDetection(
  pipeline: PipelineEntityLike | null | undefined
): PipelineEntityLike | null {
  if (!pipeline) {
    return null;
  }

  let routes = pipeline.restApiTriggerRoutes;
  if (!hasRestApiTriggerRoutes(routes)) {
    const derivedRoutes = deriveRestApiTriggerRoutesFromFlowSpec(
      pipeline.flowSpec
    );
    if (!derivedRoutes?.length) {
      return pipeline;
    }
    routes = derivedRoutes;
  }

  if (flowSpecHasApiRouter(pipeline.flowSpec)) {
    const activeMethodIds = collectConnectedApiMethodIdsFromFlowSpec(
      pipeline.flowSpec
    );
    routes = pruneRestApiTriggerRoutes(routes, activeMethodIds, {
      hasApiRouter: true
    });
  }

  if (routes === pipeline.restApiTriggerRoutes) {
    return pipeline;
  }

  return { ...pipeline, restApiTriggerRoutes: routes };
}

export function pipelineHasApiTriggerFlag(
  pipeline:
    | Pick<PipelineEntityLike, 'hasApiTrigger' | 'isUsingApiTrigger'>
    | null
    | undefined
): boolean {
  if (!pipeline) {
    return false;
  }
  return pipeline.hasApiTrigger === true || pipeline.isUsingApiTrigger === true;
}

export function isApiTriggerPipeline(
  pipeline: PipelineEntityLike | null | undefined
): boolean {
  const prepared = preparePipelineForApiTriggerDetection(pipeline);
  if (!prepared) {
    return false;
  }
  if (pipelineHasApiTriggerFlag(prepared)) {
    return true;
  }
  if (pipelineHasApiFirstMarkers(prepared)) {
    return true;
  }
  if (triggerSpecLooksLikeApiFirstDraft(prepared.triggerSpec)) {
    return true;
  }
  return false;
}

// BACKEND-API-TRIGGER: remove shouldDisplayTriggerAsApi when catalog has a separate
// `api` trigger and pipelines return triggerSpec.name `api`.
// Also remove: resolveTriggerTooltipName.ts, TriggerIcon `pipeline` prop, triggerIconPipeline
// in Card/PipelineInfosCell/DeploymentCard/cellRenderers, synthetic `api` clone in connector store.
// Keep isApiTriggerPipeline (modal routing) and isUsingApiTrigger on save.
export function shouldDisplayTriggerAsApi(
  pipeline: PipelineEntityLike | null | undefined
): boolean {
  if (!pipeline) {
    return false;
  }
  if (pipeline.triggerSpec?.name === 'api') {
    return true;
  }
  return isApiTriggerPipeline(pipeline);
}

export function resolvePipelineFromPipelinesState(
  byId: Record<string, PipelineEntityLike> | undefined,
  id: string
): PipelineEntityLike | undefined {
  if (!byId || !id) {
    return undefined;
  }
  const direct = byId[id];
  if (direct) {
    return direct;
  }
  return Object.values(byId).find(p => {
    if (!p) {
      return false;
    }
    const internalId = Reflect.get(p, '_id') as string | undefined;
    return internalId === id || p.id === id;
  });
}
